import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, Leaf } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PasswordChangeModal from "@/components/PasswordChangeModal";
import GracePeriodBanner from "@/components/GracePeriodBanner";
import { Button } from "@/components/ui/button";

export default function DashboardLayout({ navItems, roleLabel, children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwModalOpen, setPwModalOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-200 bg-white transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-700 text-white">
            <Leaf className="h-4 w-4" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 leading-tight">RootLyst</p>
            <p className="text-xs text-slate-400 leading-tight">{roleLabel}</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1 p-4">
          {navItems.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-teal-700 text-white shadow-sm"
                    : "text-slate-600 hover:translate-x-0.5 hover:bg-teal-50/70 hover:text-teal-800"
                }`}
              >
                <Icon className={`h-4 w-4 transition-colors duration-200 ${active ? "text-white" : "text-slate-400 group-hover:text-teal-700"}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-slate-200 p-4">
          <div className="mb-3 px-1">
            <p className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          {user?.role !== "RECEPTIONIST" && (
            <Button
              variant="outline"
              data-testid="reset-own-password-btn"
              className="mb-2 w-full justify-start gap-2 border-slate-200 text-slate-600"
              onClick={() => setPwModalOpen(true)}
            >
              Reset Password
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="logout-btn"
            className="w-full justify-start gap-2 border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <div className="min-w-0 flex-1 lg:ml-0">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <p className="font-semibold text-teal-700">RootLyst</p>
        </div>
        <main className="min-w-0 p-4 sm:p-6 lg:p-8">
          <GracePeriodBanner />
          {children}
        </main>
      </div>

      <PasswordChangeModal open={pwModalOpen} onOpenChange={setPwModalOpen} />
    </div>
  );
}
